import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, Camera, Wand2 } from "lucide-react";

import useImageToText from "../../hooks/useImageToText";
import { useNotification } from "../../contexts/NotificationContext";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

interface ParsedData {
  name?: string;
  phone?: string | number;
  [key: string]: any;
}

export default function RecognizeClientPage() {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [message, setMessage] = useState<string>("");
  const [tab, setTab] = useState("image");
  const [processing, setProcessing] = useState(false);

  const { ocrResult, loading, progress, error, extractTextFromImage } =
    useImageToText();

  const { showNotification } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedImage) {
      extractTextFromImage(selectedImage);
    }
  }, [extractTextFromImage, selectedImage]);

  useEffect(() => {
    if (ocrResult) {
      setMessage(ocrResult);
    }
  }, [ocrResult]);

  // Limpar mensagem quando trocar de aba
  useEffect(() => {
    if (tab === "text" && ocrResult) {
      // Se mudou para texto e há resultado OCR, manter
      return;
    }
    if (tab === "image" && !selectedImage) {
      setMessage("");
    }
  }, [tab, selectedImage, ocrResult]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedImage(event.target.files[0]);
    }
  };

  const handleProcessData = async () => {
    if (!message) return;
    setProcessing(true);
    showNotification("info", "Processando informações...");

    try {
      const response = await fetch(
        "https://extractmessagefields-oiocslysma-uc.a.run.app",
        {
          method: "POST",
          body: JSON.stringify({ message }),
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        throw new Error("Falha ao processar");
      }

      const receivedData = await response.json();
      let parsedData: ParsedData = {};

      // Se houver erro no processamento
      if (receivedData.error) {
        // Tentar extrair JSON da resposta de erro se existir
        if (receivedData.rawResponse || receivedData.cleanedResponse) {
          try {
            const rawText =
              receivedData.rawResponse || receivedData.cleanedResponse || "";
            let cleanedMessage = rawText
              .replace(/```json\s*/g, "")
              .replace(/```\s*/g, "")
              .replace(/^json\s*/g, "")
              .trim();

            // Tentar encontrar JSON no texto
            let jsonMatch = cleanedMessage.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              cleanedMessage = jsonMatch[0];
            } else {
              // Tentar encontrar qualquer JSON válido
              const jsonStart = cleanedMessage.indexOf("{");
              const jsonEnd = cleanedMessage.lastIndexOf("}");
              if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
                cleanedMessage = cleanedMessage.substring(
                  jsonStart,
                  jsonEnd + 1
                );
              }
            }

            if (cleanedMessage) {
              parsedData = JSON.parse(cleanedMessage) as ParsedData;
              showNotification(
                "warning",
                "Algumas informações podem estar incompletas. Revise os dados antes de salvar."
              );
            } else {
              throw new Error("Não foi possível extrair dados válidos");
            }
          } catch (parseError: any) {
            console.error("Parse error:", parseError);
            console.error("Received data:", receivedData);
            showNotification(
              "error",
              "Não foi possível processar as informações. A IA não conseguiu extrair dados válidos da mensagem. Tente reformular a mensagem ou adicionar mais detalhes."
            );
            setProcessing(false);
            return;
          }
        } else {
          showNotification(
            "error",
            "Erro ao processar: " + (receivedData.error || "Erro desconhecido")
          );
          setProcessing(false);
          return;
        }
      } else {
        parsedData = receivedData as ParsedData;
      }

      // Validar se pelo menos alguns campos essenciais foram extraídos
      if (!parsedData.name && !parsedData.phone) {
        showNotification(
          "warning",
          "Algumas informações podem estar faltando. Revise os dados antes de salvar."
        );
      }

      showNotification("success", "Informações processadas com sucesso!");
      navigate("/admin-management/add", {
        state: { data: parsedData },
      });
    } catch (err: any) {
      console.error("Error processing data:", err);

      let errorMessage = "Erro desconhecido ao processar";

      if (err.message) {
        errorMessage = err.message;
      } else if (err.response) {
        errorMessage = `Erro do servidor: ${err.response.status} - ${err.response.statusText}`;
      } else if (err.networkError) {
        errorMessage =
          "Erro de conexão. Verifique sua internet e tente novamente.";
      }

      showNotification("error", `Erro ao processar: ${errorMessage}`);
    } finally {
      setProcessing(false);
    }
  };

  const goBack = () => navigate("/admin-management/upcoming");

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-white">
      <header className="mb-6 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/10"
          onClick={goBack}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-white/60">
            Assistente
          </p>
          <h1 className="text-2xl font-semibold">Adicionar com IA</h1>
        </div>
      </header>

      <div className="space-y-6 rounded-3xl border border-white/10 bg-white/5 p-4">
        <p className="text-sm text-white/70">
          Adicione uma reserva carregando um print/Imagem ou colando o texto
          direto da conversa.
        </p>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white/10 text-white">
            <TabsTrigger value="image">Usar Imagem</TabsTrigger>
            <TabsTrigger value="text">Usar Texto</TabsTrigger>
          </TabsList>
          <TabsContent value="image" className="space-y-4">
            {selectedImage ? (
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4">
                <img
                  src={URL.createObjectURL(selectedImage)}
                  alt="Selecionada"
                  className="mx-auto max-h-80 w-full rounded-2xl object-contain"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-white/20 p-8 text-white/40">
                <Camera className="h-12 w-12" />
                <p>Sua imagem aparecerá aqui.</p>
              </div>
            )}

            {!loading && (
              <Button
                variant="outline"
                className="w-full border-white/20 bg-white/10 text-white hover:bg-white/20"
                asChild
              >
                <label className="cursor-pointer">
                  {selectedImage ? "Alterar imagem" : "Adicionar imagem"}
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                </label>
              </Button>
            )}

            {loading && (
              <div className="space-y-2">
                <p className="text-center text-sm text-white/60">
                  Carregando...
                </p>
                <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {error && (
              <p className="text-center text-sm text-red-400">Erro: {error}</p>
            )}
          </TabsContent>
          <TabsContent value="text">
            <textarea
              className="mt-2 min-h-[120px] w-full rounded-2xl border border-white/10 bg-transparent px-3 py-2 text-sm text-white placeholder:text-white/50"
              placeholder="Cole o texto com as informações da reserva."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </TabsContent>
        </Tabs>

        <Button
          className="w-full bg-gradient-to-r from-purple-600 to-blue-500 text-white hover:opacity-90"
          onClick={handleProcessData}
          disabled={loading || !message || processing}
        >
          <Wand2 className="mr-2 h-4 w-4" /> Processar
        </Button>
      </div>

      <div className="mt-6 rounded-3xl border border-white/10 bg-white/10 p-4 text-sm text-white/80">
        <p className="font-semibold">Aviso</p>
        <p>
          Este recurso utiliza Inteligência Artificial. Sempre valide as
          informações antes de concluir a reserva.
        </p>
      </div>
    </div>
  );
}
